import { set } from 'vue';
import RestaurantsService from '@/api/services/restaurants.service';
import type { IRestaurant } from '@/interfaces/IRestaurant';
import { useRestaurantStore } from '@/stores/restaurant.store';

/**
 * Writes the storefront look of a fresh `/full` answer into the store's
 * restaurant, so every reader (the «Diseño y estilo» cards and cover photo,
 * GPTHelp's `restaurant.design`) sees the same look. `banner` goes with
 * `bannerMeta`: the photo and its focal point always come from one answer.
 * Vue 2: `set` keeps keys the object did not have reactive.
 */
export function applyStoreLook(target: IRestaurant, fresh: IRestaurant): void {
  set(target, 'storefrontStyle', fresh.storefrontStyle);
  set(target, 'logoColor', fresh.logoColor ?? null);
  set(target, 'banner', fresh.banner);
  set(target, 'bannerMeta', fresh.bannerMeta ?? null);
}

let refreshRequest = 0;

/**
 * Reads the store's look again from `/full` (the request the store load
 * uses) after a write that changed what it is derived from
 * (`lookInputsChanged`), and writes it into the store's restaurant while that
 * is still the same store and no newer refresh started. It only writes the
 * store, never a page's state, so the caller does not wait for it. A failure
 * is logged: the next `/full` load (a reload, «Diseño y estilo») brings the
 * look.
 */
export async function refreshStoreLook(
  store: Pick<IRestaurant, 'id' | 'stringId'>
): Promise<void> {
  const restaurantStore = useRestaurantStore();
  const request = ++refreshRequest;
  try {
    const fresh = await RestaurantsService.getFullRestaurantBySlug(
      store.stringId
    );
    const current = restaurantStore.restaurant;
    if (
      request !== refreshRequest ||
      !current ||
      current.id !== store.id ||
      !fresh ||
      fresh.id !== store.id
    )
      return;
    applyStoreLook(current, fresh);
  } catch (error) {
    console.error('[design] store look refresh', error);
  }
}
